import React from 'react';
import PropTypes from 'prop-types';
import { navigate } from 'gatsby';
import { getFacetedQueryParamValuesString, getRemainingFacets } from 'utils/helpers';
import isEmpty from 'lodash/isEmpty';
import { productItems, allVersions, findProductItemFromBasePath } from 'config/productItems';
import { docTypes, docTypesPretty } from 'config/docTypes';
import { appTypeItems, hideAppTypes } from 'config/appTypes';
import { defaultSearchParams, paramNames } from 'config/search';
// import OptionsFallback from './OptionsFallback.component'
import OptionsChecker from './OptionsChecker';

// const OptionsChecker = React.lazy(() => import('./OptionsChecker'))

function cleanParams(paramKey, productKey, value, newFacetedQueryParams){
  const foundItemIndex = newFacetedQueryParams.findIndex(item => item.includes(paramKey))
  if(foundItemIndex < 0) return 
  const currentItemString = getFacetedQueryParamValuesString( 
    newFacetedQueryParams[foundItemIndex],
  );
  const currentItemParamValues = currentItemString.split(',');

  // Get the current product being removed to access its objective/persona values to filter
  const foundProductWithItemsToRemove = findProductItemFromBasePath(value)
  const hasProperties = foundProductWithItemsToRemove[productKey]
  if(hasProperties === undefined) return

  const arrayOfItemsToConvert = foundProductWithItemsToRemove[productKey].map(element => `"${element.tag}"`)
  const stringOfItemsToRemove = arrayOfItemsToConvert.join(',')
  // Only include values in newFacetedQueryParams that are not in currently removed product's param values 
  const replacedItems = currentItemParamValues.filter(element => !stringOfItemsToRemove.includes(element))
  const cleanItemsString = replacedItems.join(',')

  if(cleanItemsString === ''){
    newFacetedQueryParams.splice(foundItemIndex, 1)
  } else {
    newFacetedQueryParams[foundItemIndex] = `${paramKey}:(${cleanItemsString})`;
  }
}


const FilterControls = React.memo(function FilterControls({ 
  searchParams = '' 
}){
  const searchParamsParser = new URLSearchParams(searchParams);
  const facetedQueryParams = searchParamsParser.getAll('fq');
  let allOtherQueryParams = {};
  // eslint-disable-next-line no-restricted-syntax
  for (const key of searchParamsParser.keys()) {
    if (key !== 'fq') allOtherQueryParams[key] = searchParamsParser.get(key);
  }
  if (isEmpty(allOtherQueryParams)) {
    allOtherQueryParams = { ...defaultSearchParams.params }; 
  }


  function setFacetedQueryParams(name, value, included, isClear){

    if(isClear){
      let url = '/search?' + new URLSearchParams(allOtherQueryParams).toString(); // eslint-disable-line prefer-template
      const remainingFacets = getRemainingFacets(name, facetedQueryParams)

      if (remainingFacets.length >= 0) {
        remainingFacets.forEach(fq => {
          url += `&fq=${fq}`;
        });
      }

      navigate(url);
      return;
    }

    const newFacetedQueryParams = [...facetedQueryParams]; // contains all current param values

    // remove the product's objectives/personas for the product that is currently being unchecked from params 
    if(name === 'productName' && !included){ 
      cleanParams('objective', 'objectives', value, newFacetedQueryParams)
      cleanParams('persona', 'personas', value, newFacetedQueryParams)
    }
    // get index of query params for products only
    const foundProductItemIndex = newFacetedQueryParams.findIndex(item => item.includes('productName'))

    // check if what is changing is a product
    if(name === 'productName' && foundProductItemIndex >= 0){
      let hasVersions = false;
      let hasAppTypes = false;

      // get string of products from query params
      const currentProductString = getFacetedQueryParamValuesString( 
        newFacetedQueryParams[foundProductItemIndex],
      );
      // split string of products into individual products in array
      const currentProductParamValues = currentProductString.split(',');
      
      // loop through products to ensure item selected has versions, ALSO need to check if other items in products have versions
      if(included){
        productItems.forEach(item => {
          if(item.versions && (item.tag === value || currentProductString.includes(item.tag))) hasVersions = true;
          if(item.apptypes && (item.tag === value || currentProductString.includes(item.tag))) hasAppTypes = true;
        })
      } else{
        currentProductParamValues.forEach(item => {
          productItems.forEach(prodItem => {
            if(value !== prodItem.tag && `"${prodItem.tag}"` === item && !!prodItem.versions) hasVersions = true;
            if(value !== prodItem.tag && `"${prodItem.tag}"` === item && !!prodItem.apptypes) hasAppTypes = true;
          })
        })
      }
      
      // Remove versions params if versions are excluded from products selected
      if(!hasVersions){
        const findVersionIndex = newFacetedQueryParams.findIndex(paramValue => {
            return paramValue.includes('productVersion')
          })
        if(findVersionIndex >= 0) newFacetedQueryParams.splice(findVersionIndex, 1)
      }

      // Remove appType params if appTypes are exlucluded from products selected
      if(!hasAppTypes){
        const findVersionIndex = newFacetedQueryParams.findIndex(paramValue => {
            return paramValue.includes('appType')
          })
        if(findVersionIndex >= 0) newFacetedQueryParams.splice(findVersionIndex, 1)
      }
    }

    // find relevant objective, persona, version, product, etc. tag name that is in params
    const foundItemIndex = newFacetedQueryParams.findIndex(item => item.includes(`${name}:(`));

    if (foundItemIndex < 0 && included) {
      newFacetedQueryParams.push(`${name}:("${value}")`);
    } else if (foundItemIndex >= 0) {
      let skip = false;
      // gets all the params of the selected facet
      const currentQueryParamValuesString = getFacetedQueryParamValuesString( 
        newFacetedQueryParams[foundItemIndex],
      );
      // split all current params from selected facet into array
      const currentQueryParamValues = currentQueryParamValuesString.split(',');

      if (included) {
        currentQueryParamValues.push(`"${value}"`);
      } else {
        // item is being removed, so find index in currentQueryParamValues of matching item to remove
        const foundQueryValueIndex = currentQueryParamValues.findIndex(queryValue => {
          return queryValue.includes(`"${value}"`)
      });

        currentQueryParamValues.splice(foundQueryValueIndex, 1);
        if (currentQueryParamValues.length === 0) {
          newFacetedQueryParams.splice(foundItemIndex, 1);
          skip = true;
        }
      }
      if (!skip) {
        const newQueryParamValuesString = currentQueryParamValues.join(',');
        newFacetedQueryParams[foundItemIndex] = `${name}:(${newQueryParamValuesString})`;
      }
    }

    // always start over at page 0 when a checkbox is clicked
    allOtherQueryParams.start = 0;
    let url = '/search?' + new URLSearchParams(allOtherQueryParams).toString(); // eslint-disable-line prefer-template
    if (newFacetedQueryParams.length > 0) {
      newFacetedQueryParams.forEach(fq => {
        url += `&fq=${fq}`;
      });
    }
    navigate(url);
  };

  const findParamValues = (paramName) => {
    const foundParam = facetedQueryParams.length > 0 && facetedQueryParams.find(fq => fq.includes(`${paramNames[paramName]}:(`));
    return foundParam ? getFacetedQueryParamValuesString(foundParam).split(',') : [];
  }


  let personasList = [];
  let objectivesList = [];
  const selectedProducts = [];

  let showVersions = false;
  let showAppTypes = false;


  const productParamValues = findParamValues('Products')   
  productItems.forEach(product => {
    if (product.searchable) {
      let checked = false;
      if (facetedQueryParams.length > 0) {
        checked = productParamValues.length > 0 && productParamValues.includes(`"${product.tag}"`);
        if(checked && product.versions) showVersions = true  
        if(checked && product.apptypes) showAppTypes = true;
      }

      selectedProducts.push({
        label: product.name,
        checked,
        name: product.tag,
      });

      if(checked){
        const personaTags = new Set(personasList.map(d => d.tag))
        personasList = [...personasList, ...product.personas.filter(d => !personaTags.has(d.tag))];

        const objectiveTags = new Set(objectivesList.map(d => d.tag))
        objectivesList = [...objectivesList, ...product.objectives.filter(d => !objectiveTags.has(d.tag))];
      }
    }
  });
 
  const typeParamValues = findParamValues('type')
  const selectedDocTypes = docTypes.map(type => {
    const checked = typeParamValues && typeParamValues.includes(`"${type}"`);
    return {
      label: docTypesPretty[type],
      checked,
      name: type,
    };
  });

  const versionParamValues = findParamValues('version')
  const selectedVersions = allVersions.map(version => {
    const checked = versionParamValues && versionParamValues.includes(`"${version}"`);
    return {
      label: version,
      checked,
      name: version,
    };
  });

  const setSelected = (prettyParam, facetList) => {
    const foundParamValues = findParamValues(prettyParam)
    const selected = facetList.filter(itm => !hideAppTypes.includes(itm.tag)).map(item => {
      const checked = foundParamValues && foundParamValues.includes(`"${item.tag}"`);
      return {
        label: item.name,
        checked,
        name: item.tag
      }
    })
    return selected
  }

  const selectedPersonas = setSelected('User Role', personasList)
  const selectedObjectives = setSelected('Objective', objectivesList)
  const selectedAppTypes = setSelected('appType', appTypeItems)


  return (
    <div style={{margin: '2em 0',}}>
      {/* <React.Suspense fallback={<OptionsFallback />}> */}
        <OptionsChecker
          title="By Product"
          options={selectedProducts}
          handleChange={setFacetedQueryParams}  // eslint-disable-line react/jsx-no-bind
          facets={paramNames.Products}
       />

        {
          showVersions &&
          <OptionsChecker
            title="By Version"
            options={selectedVersions}
            handleChange={setFacetedQueryParams} // eslint-disable-line react/jsx-no-bind
            facets={paramNames.version}
          />
        }

        {
          showAppTypes &&
          <OptionsChecker
            title="By App Type"
            options={selectedAppTypes}
            handleChange={setFacetedQueryParams} // eslint-disable-line react/jsx-no-bind
            facets={paramNames.appType}
          />
        }

        <OptionsChecker 
          title="By Type" 
          options={selectedDocTypes} 
          handleChange={setFacetedQueryParams} // eslint-disable-line react/jsx-no-bind
          facets={paramNames.type}
        />

        {
          objectivesList.length > 0 &&
          <OptionsChecker
            title="By Objective"
            options={selectedObjectives}
            handleChange={setFacetedQueryParams} // eslint-disable-line react/jsx-no-bind
            facets={paramNames.Objective}
          />
        }

        {
          personasList.length > 0 &&
          <OptionsChecker
            title="By User Role"
            options={selectedPersonas}
            handleChange={setFacetedQueryParams} // eslint-disable-line react/jsx-no-bind
            facets={paramNames['User Role']}
          />
        }
      {/* </React.Suspense> */}
    </div>
  );
})


FilterControls.propTypes = {
  searchParams: PropTypes.string,
};

export default FilterControls;
